// Global
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { getTestProps } from 'lib/testing/utils';
import { tv } from 'tailwind-variants';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const ClientStoriesCarouselItem = (props)=>{
    const isEditing = useIsEditing();
    const { image, jobtitle, link, name, quoteText } = props?.fields || {};
    const { RenderingIdentifier } = props?.params || {};
    const styles = parseStyleParams(props.params, [
        'cta1'
    ]);
    const { container, imageSection, contentSection, contentTop, contentBottom, imageWrapper, testimonialQuote, expertDetails, expertNameTV, expertRoleTV, separator, ctaWrapper, ctaLink } = clientStoriesCarouselItemTailwindVariants();
    return /*#__PURE__*/ _jsxs("article", {
        className: container(),
        "data-component": "authorable/shared/lists/expertcard",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`expert-card-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: imageSection(),
                children: image?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: image,
                    className: imageWrapper(),
                    ...getTestProps(`expert-image`)
                }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: {
                        value: {
                            src: '/assets/profile_placeholder.png',
                            alt: 'No image'
                        }
                    },
                    className: imageWrapper(),
                    ...getTestProps(`related-profile-image-placeholder`)
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: contentSection(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentTop(),
                        children: [
                            quoteText?.value && /*#__PURE__*/ _jsx(RichTextWrapper, {
                                field: quoteText,
                                className: `text-component-expert-testimonial ${testimonialQuote()}`,
                                ...getTestProps(`expert-testimonial`)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: expertDetails(),
                                children: /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: expertNameTV(),
                                            field: name,
                                            tag: "div",
                                            ...getTestProps(`expert-name`)
                                        }),
                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                            className: expertRoleTV(),
                                            field: jobtitle,
                                            tag: "div",
                                            ...getTestProps(`expert-role`)
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentBottom(),
                        children: [
                            link?.value?.href && /*#__PURE__*/ _jsx("div", {
                                className: separator()
                            }),
                            link?.value?.href && /*#__PURE__*/ _jsx("div", {
                                className: ctaWrapper(),
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-card-item-button-1-color",
                                    className: ctaLink(),
                                    field: link,
                                    ctaIcon: "arrow-right",
                                    ctaIconSize: "s",
                                    ctaStyle: getCtaStyle(styles.cta1, 'tertiary'),
                                    "aria-label": link?.value?.text ? `${link?.value?.text} for ${name?.value || 'expert'}` : `Find out more about ${name?.value || 'expert'}`,
                                    ...getTestProps(`expert-cta`)
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default ClientStoriesCarouselItem;
const clientStoriesCarouselItemTailwindVariants = tv({
    slots: {
        container: [
            'flex',
            'flex-col',
            'w-full',
            'h-full',
            'overflow-hidden',
            'rounded-general-border-radius-card',
            'shadow-color-black-alpha-10',
            'shadow-[0_0_8px_0]'
        ],
        imageSection: [
            'relative',
            'w-full',
            'overflow-hidden',
            'aspect-[4/3]',
            'rounded-t-general-border-radius-card'
        ],
        contentSection: [
            'flex',
            'flex-col',
            'flex-1',
            'w-full',
            'gap-spacing-spacing-24',
            'py-component-card-item-landscape-content-1-col-padding-y',
            'px-component-card-item-landscape-content-1-col-padding-x',
            'rounded-b-general-border-radius-card',
            'bg-white'
        ],
        contentTop: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        contentBottom: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'mt-auto'
        ],
        imageWrapper: [
            'w-full',
            'h-full',
            'object-cover'
        ],
        testimonialQuote: [
            'text-color-core-color-1-Navy',
            'font-typography-font-family-header',
            'text-typography-font-size-header-small',
            'font-normal',
            'leading-typography-line-height-small',
            'text-left',
            'relative'
        ],
        expertDetails: [
            'flex',
            'items-start',
            'justify-between',
            'gap-spacing-spacing-16'
        ],
        expertNameTV: [
            'text-color-core-color-1-Navy',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'text-left',
            'leading-normal'
        ],
        expertRoleTV: [
            'text-color-core-color-1-Navy',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-normal',
            'leading-normal',
            'text-left'
        ],
        separator: [
            'w-full',
            'h-[1px]',
            'bg-color-core-color-4-SkyBlue'
        ],
        ctaWrapper: [
            'flex',
            'items-center',
            'justify-between',
            'self-stretch'
        ],
        ctaLink: [
            '!text-color-core-color-1-Navy',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-typography-line-height-xxsmall',
            'flex',
            'items-center',
            'justify-between',
            'w-full',
            'transition-opacity',
            'duration-200',
            '[&:hover_svg]:!text-color-core-color-1-Navy',
            '!p-0'
        ]
    }
});
